import { ReactNode, useState } from 'react'
import {
  Button,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'

import {
  AutoStatsDevice,
  DeviceReport,
  DeviceReportMountpoint,
  DeviceReportRover,
  useAutoStatsDeviceReport,
} from 'api'
import { strings } from 'strings'

import jalali from 'jalaali-js'

import {
  EPOCH_DAY_KEY,
  formatJalaliDateTime,
  toPersianDigits,
} from './analyticsShared'
import { MapListColumn, MapListView } from './MapListView'

interface Props {
  open: boolean
  onClose: () => void
  device: AutoStatsDevice | null
  from: string
  to: string
}

export function DeviceReportDialog({ open, onClose, device, from, to }: Props) {
  const classes = useStyles()
  const rangeLabel = formatRangeLabel(from, to)
  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen
      PaperProps={{ style: { direction: 'rtl' } }}
    >
      <DialogTitle className={classes.dialogTitle}>
        {device
          ? `${strings.deviceActivityReport} — ${device.serial} — ${rangeLabel}`
          : strings.deviceActivityReport}
      </DialogTitle>
      <DialogContent dividers className={classes.dialogContent}>
        {open && device && (
          <ReportBody device={device} from={from} to={to} />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} size="small" variant="outlined">
          {strings.close}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

function ReportBody({
  device,
  from,
  to,
}: {
  device: AutoStatsDevice
  from: string
  to: string
}) {
  const classes = useStyles()
  const { report, loading, error } = useAutoStatsDeviceReport(
    device.serial,
    from,
    to,
  )

  if (loading) {
    return (
      <div className={classes.status}>
        <CircularProgress size={24} />
        <Typography>{strings.loading}</Typography>
      </div>
    )
  }
  if (error) {
    return (
      <Typography className={classes.status}>
        {strings.error}: {String((error as any)?.message ?? error)}
      </Typography>
    )
  }
  if (!report) {
    return (
      <Typography className={classes.status}>
        {strings.noSessionsInRange}
      </Typography>
    )
  }

  return (
    <div className={classes.bodyWrap}>
      <SummaryHeader device={device} report={report} />
      <MountpointsSection report={report} />
      <RoversSection report={report} />
    </div>
  )
}

function SummaryHeader({
  device,
  report,
}: {
  device: AutoStatsDevice
  report: DeviceReport
}) {
  const classes = useStyles()
  const roverCount = report.rovers?.length ?? 0
  return (
    <div className={classes.summary}>
      <div className={classes.summaryHead}>
        <Typography className={classes.summaryName}>
          {report.serial}
        </Typography>
      </div>
      <div className={classes.summaryGrid}>
        <SummaryStat
          label={strings.ownerDisplayName}
          hint={strings.hintDeviceOwner}
          value={device.ownerDisplayName || '—'}
        />
        <SummaryStat
          label={strings.ownerPhoneNumber}
          hint={strings.hintDeviceOwnerPhone}
          value={device.ownerPhoneNumber || '—'}
          ltr
        />
        <SummaryStat
          label={strings.mountpointsServed}
          hint={strings.hintDeviceMountpointsServed}
          value={toPersianDigits(report.mountpointCount ?? 0)}
        />
        <SummaryStat
          label={strings.totalRoverConnections}
          hint={strings.hintDeviceTotalRoverConnections}
          value={toPersianDigits(device.totalRoverConnections)}
        />
        <SummaryStat
          label={strings.rovers}
          hint={strings.hintDeviceRoversCount}
          value={toPersianDigits(roverCount)}
        />
        <SummaryStat
          label={strings.totalRoverAirtime}
          hint={strings.hintDeviceTotalRoverAirtime}
          value={renderDurationCell(report.totalRoverDurationSeconds ?? 0)}
        />
      </div>
    </div>
  )
}

function SummaryStat({
  label,
  hint,
  value,
  ltr,
}: {
  label: string
  hint?: string
  value: ReactNode
  ltr?: boolean
}) {
  const classes = useStyles()
  return (
    <div className={classes.statBox}>
      <div className={classes.statLabelRow}>
        <Typography className={classes.statLabel}>{label}</Typography>
        {hint && <HintIcon text={hint} />}
      </div>
      <Typography
        className={classes.statValue}
        style={ltr ? { direction: 'ltr', textAlign: 'right' } : undefined}
      >
        {value}
      </Typography>
    </div>
  )
}

function HintIcon({ text }: { text: string }) {
  const classes = useStyles()
  return (
    <Tooltip title={text} arrow placement="top">
      <InfoOutlinedIcon className={classes.hintIcon} fontSize="small" />
    </Tooltip>
  )
}

function MountpointsSection({ report }: { report: DeviceReport }) {
  const classes = useStyles()
  const [expanded, setExpanded] = useState(true)
  const items = report.mountpoints ?? []
  if (items.length === 0) return null
  const sorted = [...items].sort(
    (a, b) => b.roverConnections - a.roverConnections,
  )
  const showSecretKey = sorted.some((m) => m.variant)
  const showCreator = sorted.some((m) => m.creatorDisplayName)

  const columns: MapListColumn<DeviceReportMountpoint>[] = [
    {
      label: strings.mountpoint,
      render: (m) => m.mountpoint,
    },
    ...(showSecretKey
      ? [
          {
            label: strings.secretKey,
            ltr: true,
            render: (m: DeviceReportMountpoint) => m.variant ?? '—',
          },
        ]
      : []),
    ...(showCreator
      ? [
          {
            label: strings.createdBy,
            render: (m: DeviceReportMountpoint) => m.creatorDisplayName ?? '—',
          },
        ]
      : []),
    {
      label: strings.totalRoverConnections,
      render: (m) => toPersianDigits(m.roverConnections),
    },
  ]

  const toggle = () => setExpanded((v) => !v)

  return (
    <section className={classes.section}>
      <div
        className={classes.sectionHeadToggle}
        role="button"
        tabIndex={0}
        onClick={toggle}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            toggle()
          }
        }}
      >
        <Typography className={classes.sectionTitle}>
          {strings.mountpointsServed} ({toPersianDigits(sorted.length)})
        </Typography>
        <IconButton size="small" aria-label={expanded ? 'collapse' : 'expand'}>
          <ExpandMoreIcon
            className={`${classes.expandIcon} ${
              expanded ? classes.expandIconOpen : ''
            }`}
          />
        </IconButton>
      </div>
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <div className={classes.mapListWrap}>
          <MapListView
            rows={sorted}
            columns={columns}
            rowKey={(m) => m.mountpointId ?? m.mountpoint}
            getLocation={(m) =>
              m.latitude !== undefined && m.longitude !== undefined
                ? [m.latitude, m.longitude]
                : null
            }
            markerColor="#198D10"
          />
        </div>
      </Collapse>
    </section>
  )
}

function roverConnections(r: DeviceReportRover): number {
  return r.totalConnections ?? r.connections ?? r.roverConnections ?? 0
}
function roverAirtime(r: DeviceReportRover): number {
  return r.totalDurationSeconds ?? r.durationSeconds ?? 0
}
function roverMax(r: DeviceReportRover): number {
  return r.maxSessionDurationSeconds ?? r.maxDurationSeconds ?? 0
}
function roverLast(r: DeviceReportRover): number {
  return r.lastConnectUnix ?? r.lastActiveUnix ?? 0
}

function RoversSection({ report }: { report: DeviceReport }) {
  const classes = useStyles()
  const [expanded, setExpanded] = useState(true)
  const items = report.rovers ?? []
  if (items.length === 0) return null
  const sorted = [...items].sort(
    (a, b) =>
      roverConnections(b) - roverConnections(a) ||
      roverAirtime(b) - roverAirtime(a),
  )
  const showPhone = sorted.some((r) => r.phoneNumber)
  const showMax = sorted.some((r) => roverMax(r) > 0)
  const showLast = sorted.some((r) => roverLast(r) > 0)

  const columns: MapListColumn<DeviceReportRover>[] = [
    {
      label: strings.fullname,
      render: (r) => r.displayName?.trim() || r.username,
    },
    {
      label: strings.username,
      ltr: true,
      render: (r) => r.username,
    },
    ...(showPhone
      ? [
          {
            label: strings.phoneNumber,
            ltr: true,
            render: (r: DeviceReportRover) => r.phoneNumber || '—',
          },
        ]
      : []),
    {
      label: strings.connectionsCount,
      render: (r) => toPersianDigits(roverConnections(r)),
      sortValue: (r) => roverConnections(r),
    },
    {
      label: strings.totalAirtime,
      render: (r) => renderDurationCell(roverAirtime(r)),
      sortValue: (r) => roverAirtime(r),
    },
    ...(showMax
      ? [
          {
            label: strings.maxDuration,
            render: (r: DeviceReportRover) => renderDurationCell(roverMax(r)),
            sortValue: (r: DeviceReportRover) => roverMax(r),
          },
        ]
      : []),
    ...(showLast
      ? [
          {
            label: strings.lastConnect,
            ltr: true,
            render: (r: DeviceReportRover) => {
              const l = roverLast(r)
              return l > 0 ? formatJalaliDateTime(l) : '—'
            },
            sortValue: (r: DeviceReportRover) =>
              roverLast(r) > 0 ? roverLast(r) : null,
          },
        ]
      : []),
  ]

  const toggle = () => setExpanded((v) => !v)

  return (
    <section className={classes.section}>
      <div
        className={classes.sectionHeadToggle}
        role="button"
        tabIndex={0}
        onClick={toggle}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            toggle()
          }
        }}
      >
        <Typography className={classes.sectionTitle}>
          {strings.roversConnectedTitle} ({toPersianDigits(sorted.length)})
        </Typography>
        <IconButton size="small" aria-label={expanded ? 'collapse' : 'expand'}>
          <ExpandMoreIcon
            className={`${classes.expandIcon} ${
              expanded ? classes.expandIconOpen : ''
            }`}
          />
        </IconButton>
      </div>
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <div className={classes.mapListWrap}>
          <MapListView
            rows={sorted}
            columns={columns}
            rowKey={(r) => r.userId}
            getLocation={(r) =>
              r.latitude !== undefined && r.longitude !== undefined
                ? [r.latitude, r.longitude]
                : null
            }
            markerColor="#7b1fa2"
          />
        </div>
      </Collapse>
    </section>
  )
}

function jalaliDayKey(dayKey: string): string {
  const [y, m, d] = dayKey.split('-').map(Number)
  const j = jalali.toJalaali(y, m, d)
  const pad = (n: number) => (n < 10 ? `0${n}` : String(n))
  return `${j.jy}-${pad(j.jm)}-${pad(j.jd)}`
}

function formatRangeLabel(from: string, to: string): string {
  const fromKey = from.slice(0, 10)
  const toKey = to.slice(0, 10)
  if (fromKey === EPOCH_DAY_KEY) return strings.allData
  if (fromKey === toKey) return jalaliDayKey(fromKey)
  return `${jalaliDayKey(fromKey)} — ${jalaliDayKey(toKey)}`
}

function formatDuration(seconds: number): string {
  if (!seconds || seconds <= 0) return '—'
  // Round to whole seconds. The API may return fractional seconds and the
  // raw modulo would otherwise leak floating-point noise into the label
  // (e.g. "3m 15.281499s").
  const total = Math.round(seconds)
  const h = Math.floor(total / 3600)
  const m = Math.floor((total % 3600) / 60)
  const s = total % 60
  if (h > 0) return `${toPersianDigits(h)}h ${toPersianDigits(m)}m`
  if (m > 0) return `${toPersianDigits(m)}m ${toPersianDigits(s)}s`
  return `${toPersianDigits(s)}s`
}

function renderDurationCell(seconds: number) {
  if (!seconds || seconds <= 0) return '—'
  const hours = (seconds / 3600).toFixed(2)
  return (
    <Tooltip title={`${hours} ${strings.durationHours}`} arrow>
      <span>{formatDuration(seconds)}</span>
    </Tooltip>
  )
}

const useStyles = makeStyles((theme) => ({
  dialogTitle: {
    padding: theme.spacing(1, 2),
  },
  dialogContent: {
    padding: 0,
  },
  bodyWrap: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    padding: theme.spacing(2),
  },
  status: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(4),
    justifyContent: 'center',
  },
  summary: {
    border: '1px solid #e0e0e0',
    borderRadius: 4,
    background: '#fafafa',
    padding: theme.spacing(1.5, 2),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  summaryHead: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  summaryName: {
    fontSize: '1.15rem',
    fontWeight: 700,
    direction: 'ltr',
  },
  summaryGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
    gap: theme.spacing(1),
  },
  statBox: {
    display: 'flex',
    flexDirection: 'column',
  },
  statLabelRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  statLabel: {
    fontSize: '0.8rem',
    color: '#666',
  },
  hintIcon: {
    fontSize: '0.95rem',
    color: '#999',
    cursor: 'help',
    verticalAlign: 'middle',
    marginInlineStart: theme.spacing(0.25),
  },
  statValue: {
    fontSize: '0.95rem',
    fontWeight: 600,
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  sectionHead: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
  },
  sectionHeadToggle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    cursor: 'pointer',
    userSelect: 'none',
    padding: theme.spacing(0.5, 1),
    borderRadius: 4,
    background: '#f5f5f5',
    border: '1px solid #e0e0e0',
    '&:hover': {
      background: '#eee',
    },
    '&:focus-visible': {
      outline: `2px solid ${theme.palette.primary.main}`,
    },
  },
  expandIcon: {
    transition: 'transform 0.2s ease',
  },
  expandIconOpen: {
    transform: 'rotate(180deg)',
  },
  sectionTitle: {
    fontSize: '1.05rem',
    fontWeight: 700,
  },
  mapListWrap: {
    height: 480,
    minHeight: 360,
    border: '1px solid #e0e0e0',
    borderRadius: 4,
    overflow: 'hidden',
    background: '#fff',
  },
}))
